package nl.appstuff.padelchecker.site

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.HTMLScriptElement

private const val SITE_URL = "https://padelchecker.nl/site"
private const val DEFAULT_OG_IMAGE = "/images/padelchecker_hero.png"

@Composable
fun PageSeo(
    title: String,
    description: String,
    canonicalPath: String,
    ogImage: String = DEFAULT_OG_IMAGE,
    lang: String = "nl",
) {
    LaunchedEffect(title, description, canonicalPath, lang) {
        document.title = title
        (document.documentElement as? HTMLElement)?.lang = lang

        upsertMeta("description", description)
        upsertMeta("robots", "index, follow")

        val fullUrl = "$SITE_URL$canonicalPath"
        val fullImage = "$SITE_URL$ogImage"

        upsertMetaProperty("og:title", title)
        upsertMetaProperty("og:description", description)
        upsertMetaProperty("og:url", fullUrl)
        upsertMetaProperty("og:image", fullImage)
        upsertMetaProperty("og:type", "website")
        upsertMetaProperty("og:locale", if (lang == "en") "en_US" else "nl_NL")
        upsertMetaProperty("og:site_name", "Padelchecker")

        upsertMeta("twitter:card", "summary_large_image")
        upsertMeta("twitter:title", title)
        upsertMeta("twitter:description", description)
        upsertMeta("twitter:image", fullImage)

        upsertCanonical(fullUrl)
    }
}

@Composable
fun StructuredData(jsonLd: String) {
    LaunchedEffect(jsonLd) {
        val existing = document.querySelector("script[type='application/ld+json'][data-seo]")
        existing?.remove()

        val script = document.createElement("script") as HTMLScriptElement
        script.type = "application/ld+json"
        script.setAttribute("data-seo", "true")
        script.text = jsonLd
        document.head?.appendChild(script)
    }
}

private fun upsertMeta(name: String, content: String) {
    val existing = document.querySelector("meta[name='$name']") as? HTMLMetaElement
    if (existing != null) {
        existing.content = content
    } else {
        val meta = document.createElement("meta") as HTMLMetaElement
        meta.name = name
        meta.content = content
        document.head?.appendChild(meta)
    }
}

private fun upsertMetaProperty(property: String, content: String) {
    val existing = document.querySelector("meta[property='$property']") as? HTMLMetaElement
    if (existing != null) {
        existing.content = content
    } else {
        val meta = document.createElement("meta") as HTMLMetaElement
        meta.setAttribute("property", property)
        meta.content = content
        document.head?.appendChild(meta)
    }
}

private fun upsertCanonical(href: String) {
    val existing = document.querySelector("link[rel='canonical']") as? HTMLLinkElement
    if (existing != null) {
        existing.href = href
    } else {
        val link = document.createElement("link") as HTMLLinkElement
        link.rel = "canonical"
        link.href = href
        document.head?.appendChild(link)
    }
}
